<template>
    <SectionBase :section="section">
        <CustomDesignAnimation :section="section" :animation-name="animationName" />
        <div
            v-for="({ module, card }, index) in visibleModules()"
            :key="module.moduleId || index"
            class="col"
            :style="columnSettings(module)"
        >
            <div
                :class="['ufo--card', cardTypeClass, animationName]"
                :style="cardStyleVars"
            >
                <GetSectionText :section-text="card?.text" />
            </div>
        </div>
    </SectionBase>
</template>

<script>
import SectionBase from "@/scripts/view/portfolio/sections/SectionBase.vue";
import GetSectionText from "@/scripts/view/portfolio/sections/_helper/GetSectionText.vue";
import {CardLayoutEnum, PageTypesEnum, SectionModuleTypeEnum} from "@/scripts/common/enums.js";
import {mapGetters} from "vuex";
import { resolveRowSpan } from "@/scripts/common/utilities/layout.js";
import {newAnimationName} from "@/scripts/common/customDesign/renderCustomDesign.js";
import CustomDesignAnimation from "@/scripts/common/customDesign/CustomDesignAnimation.vue";

const LAYOUT_COLUMNS = {
    [CardLayoutEnum.ROW_2]: 2,
    [CardLayoutEnum.ROW_3]: 3,
    [CardLayoutEnum.GRID_2X2]: 2,
    [CardLayoutEnum.GRID_3X2]: 3
};

export default {
    name: "Cards",
    components: {
        CustomDesignAnimation,
        SectionBase,
        GetSectionText
    },
    props: ["section"],
    data() {
        return {
            animationName: newAnimationName(),
            equalizeFrame: null,
            onWindowResize: null
        };
    },
    mounted() {
        this.onWindowResize = () => this.scheduleCardEqualize();
        window.addEventListener("resize", this.onWindowResize);
        this.scheduleCardEqualize();
    },
    beforeUnmount() {
        if (this.onWindowResize) {
            window.removeEventListener("resize", this.onWindowResize);
        }
        if (this.equalizeFrame) {
            cancelAnimationFrame(this.equalizeFrame);
            this.equalizeFrame = null;
        }
    },
    methods: {
        scheduleCardEqualize() {
            if (typeof window === "undefined") return;
            if (this.equalizeFrame) return;
            this.equalizeFrame = requestAnimationFrame(() => {
                this.equalizeFrame = null;
                this.syncCardEqualHeight();
            });
        },
        syncCardEqualHeight() {
            const root = this.$el;
            if (!root) return;
            if (window.innerWidth < 768) {
                root.style.removeProperty("--card-equal-height");
                return;
            }
            root.style.removeProperty("--card-equal-height");
            const cards = root.querySelectorAll(".ufo--card");
            if (!cards.length) return;
            let maxHeight = 0;
            cards.forEach((card) => {
                maxHeight = Math.max(maxHeight, card.offsetHeight || 0);
            });
            if (maxHeight > 0) {
                root.style.setProperty("--card-equal-height", `${maxHeight}px`);
            }
        },
        visibleModules() {
            const columns = LAYOUT_COLUMNS[this.section.layout] || 2;
            const modules = this.section.layoutSettings?.modules?.filter(
                module => module.type !== SectionModuleTypeEnum.SPACER
            ) || [];
            return modules.map((module, index) => ({
                module: {
                    ...module,
                    gridSettings: {
                        ...module?.gridSettings,
                        ...(Number.isFinite(module?.gridSettings?.rowStart) && Number.isFinite(module?.gridSettings?.rowEnd)
                            ? {}
                            : {
                                rowStart: Math.floor(index / columns) + 1,
                                rowEnd: Math.floor(index / columns) + 2
                            })
                    }
                },
                card: this.section.cards?.[index] || null
            }));
        },
        columnSettings(module) {
            const { rowStart, rowEnd } = resolveRowSpan(module.gridSettings);
            return {
                '--columnStart': module.gridSettings?.columnStart,
                '--columnEnd': module.gridSettings?.columnEnd,
                '--rowStart': rowStart,
                '--rowEnd': rowEnd,
                '--height': module.gridSettings?.height ? `${module.gridSettings?.height}px` : null,
            };
        },
        resolveModuleBackgroundColor() {
            const colorData = this.section.moduleBackgroundSettings?.color;
            if (!colorData) return null;
            if (Number.isFinite(colorData.paletteColorIndex)) {
                return this.colorPalette?.colors?.[colorData.paletteColorIndex] || null;
            }
            return colorData.color || null;
        }
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        project() {
            return this.viewData.project || this.$store.state.project;
        },
        portfolio() {
            return this.viewData.portfolio || this.$store.state.portfolio;
        },
        isCaseStudy() {
            return this.project?.type === PageTypesEnum.CASE_STUDY;
        },
        usingGlobalColorStyles() {
            return this.project.type === PageTypesEnum.CASE_STUDY && this.project.designSettings.globalColorStyles;
        },
        colorPalette() {
            return this.isCaseStudy && !this.usingGlobalColorStyles ?
                this.project.designSettings.colorPalette :
                this.portfolio.designSettings.colorPalette;
        },
        cardTypeClass() {
            const type = this.section.moduleBackgroundSettings?.moduleBackgroundType;
            if (type === "OUTLINE") return "card-type--outline";
            if (type === "SOLID") return "card-type--fill";
            return "card-type--none";
        },
        cardStyleVars() {
            return {
                '--card-background-color': this.resolveModuleBackgroundColor(),
                '--card-corner-radius': this.section.moduleBackgroundSettings?.cornerRadiusPx != null
                    ? `${this.section.moduleBackgroundSettings.cornerRadiusPx}px`
                    : null,
                '--card-outline-width': this.section.moduleBackgroundSettings?.stroke?.strength != null
                    ? `${this.section.moduleBackgroundSettings.stroke.strength}px`
                    : null
            };
        }
    }
};
</script>
